import React from "react"
import { makeAutoObservable } from "mobx"
import { t } from "@lingui/macro"

import {
    ConnectorAdminService,
    IntegrationAdminService,
    AdminService,
} from "src/api"
import { avy_api_pkg_common_IntegrationWithType } from "src/api/models/avy_api_pkg_common_IntegrationWithType"
import { fastapi_Fi2Property } from "src/api/models/fastapi_Fi2Property"
import { IPropertyOwner } from "src/views/client-management/store"
import { reportError } from "src/lib/report"
import { createLoadingKeys } from "src/lib/loading"
import { loads } from "src/channel/utils"
import { Channel } from "src/channel"

export class FastApiPropertyLoaderModalStore {
    static Context =
        React.createContext<FastApiPropertyLoaderModalStore | null>(null)

    static LoadingKeys = createLoadingKeys(
        "loadIntegrations",
        "loadFastApiProperties",
        "createProperty",
    )

    propertyOwner: IPropertyOwner | null = null
    selectedIntegrationId: number = 0
    fastApiIntegrations: avy_api_pkg_common_IntegrationWithType[] = []
    fastApiProperties: fastapi_Fi2Property[] = []
    isLoading: boolean = false
    currentAccessGroupId: number = 0
    legalEntityId: number | undefined = undefined
    onPropertiesUpdated?: () => void
    creationProgress: { current: number; total: number } = {
        current: 0,
        total: 0,
    }

    constructor() {
        makeAutoObservable(this)
    }

    @loads(() => FastApiPropertyLoaderModalStore.LoadingKeys.loadIntegrations)
    async init(
        propertyOwner: IPropertyOwner,
        currentAccessGroupId: number,
        legalEntityId?: number,
        onPropertiesUpdated?: () => void,
        preFilteredConnectors?: avy_api_pkg_common_IntegrationWithType[],
    ) {
        this.propertyOwner = propertyOwner
        this.currentAccessGroupId = currentAccessGroupId
        this.legalEntityId = legalEntityId
        this.onPropertiesUpdated = onPropertiesUpdated

        try {
            // Use pre-filtered connectors if provided, otherwise load and filter them
            if (preFilteredConnectors != null) {
                this.fastApiIntegrations = preFilteredConnectors
            } else {
                // Fallback to original logic if no pre-filtered connectors provided
                const allIntegrations =
                    await ConnectorAdminService.getV1AdminConnectorIntegration({
                        type: "fastapi",
                    })

                this.fastApiIntegrations = allIntegrations.filter(
                    (integration) =>
                        integration.access_group_id === currentAccessGroupId,
                )
            }

            if (
                propertyOwner.fastapiConnectorIntegrationId != null &&
                propertyOwner.fastapiConnectorIntegrationId > 0
            ) {
                const propertyOwnerIntegration = this.fastApiIntegrations.find(
                    (integration) =>
                        integration.integration_id ===
                        propertyOwner.fastapiConnectorIntegrationId,
                )

                if (propertyOwnerIntegration != null) {
                    this.selectedIntegrationId =
                        propertyOwner.fastapiConnectorIntegrationId
                }
            }
        } catch (error) {
            reportError(
                t`fastapi-property-loader.failed-to-load-integrations`,
                error,
            )
        }
    }

    setSelectedIntegrationId(integrationId: number) {
        this.selectedIntegrationId = integrationId
    }

    @loads(
        () => FastApiPropertyLoaderModalStore.LoadingKeys.loadFastApiProperties,
    )
    async loadFastApiProperties() {
        try {
            const properties =
                await IntegrationAdminService.getV1AdminIntegrationFastapiConnectorProperties(
                    {
                        connectorId: this.selectedIntegrationId,
                    },
                )
            this.fastApiProperties = properties
        } catch (error) {
            reportError(
                t`fastapi-property-loader.failed-to-load-properties`,
                error,
            )
        }
    }

    @loads(() => FastApiPropertyLoaderModalStore.LoadingKeys.createProperty)
    async createPropertiesFromFastApi(
        fastApiProperties: fastapi_Fi2Property[],
        _propertyOwnerId: number,
        onProgress?: (current: number, total: number) => void,
    ) {
        try {
            this.creationProgress = {
                current: 0,
                total: fastApiProperties.length,
            }
            const createdProperties = []

            // Create properties one by one to track progress
            for (let i = 0; i < fastApiProperties.length; i++) {
                const fastApiProperty = fastApiProperties[i]

                // Update progress
                this.creationProgress = {
                    current: i,
                    total: fastApiProperties.length,
                }
                onProgress?.(i, fastApiProperties.length)

                const request = [
                    {
                        name: fastApiProperty.name ?? "",
                        city: fastApiProperty.address?.city ?? "",
                        external_id: fastApiProperty.id ?? "",
                        legal_entity_id: this.legalEntityId,
                        integration_id: undefined,
                        integration_active: false,
                        self_onboarding_enabled: false,
                    },
                ]

                const response = await AdminService.postV1AdminProperty({
                    request,
                })

                createdProperties.push({
                    propertyId: response?.property_ids?.[0] ?? 0,
                    name: fastApiProperty.name ?? "",
                    city: fastApiProperty.address?.city ?? "",
                    externalId: fastApiProperty.id ?? "",
                })

                // Update progress after creation
                this.creationProgress = {
                    current: i + 1,
                    total: fastApiProperties.length,
                }
                onProgress?.(i + 1, fastApiProperties.length)
            }

            // Send channel update to refresh the properties list
            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "properties",
                    action: "create",
                    item: {
                        id: createdProperties[0]?.propertyId ?? 0,
                        name: createdProperties[0]?.name ?? "",
                    },
                },
            })

            // Call the callback to refresh existing properties
            if (this.onPropertiesUpdated != null) {
                this.onPropertiesUpdated()
            }

            return createdProperties
        } catch (error) {
            reportError(
                t`fastapi-property-loader.failed-to-create-property`,
                error,
            )
            return []
        }
    }

    get loading() {
        return {
            is: (_key: string) => this.isLoading,
        }
    }
}
